import { useCallback, useEffect, useState } from 'react';
import queryString from 'query-string';
import { format } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IconLoader,
  DateInputRange,
  ButtonPseudo,
  IconRefresh,
  ButtonSize,
  IconDownload,
  PermissionEnum,
  PermissionDenied,
  ToggleInput,
  Checkbox,
  ButtonSecondary,
  ButtonPrimary,
  SidePanelType,
  SidePanel,
  ResponseError,
  getResponseErrors,
  AccountingJob,
  AccountingJobProps,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import BankAndPsp from '../../components/bank-and-psp/bank-and-psp';
import { AccountType } from '../types';
import { changeVisibility } from './store/slice';
import {
  useLazyGetAccountingJobsInProgressQuery,
  useLazyGetAccountingJobsInProgressStatesQuery,
} from '../../store/dictionaries/service';
import {
  useLazyGetBanksReportQuery,
  useExportBanksReportMutation,
  useExportBanksReportWithAccountsMutation,
} from './store/services';
import { Banks } from './types';
import { setNotificationVisible } from '../../../../../store/slice';
import { clearAccountingJobs } from '../../store/dictionaries/slice';
import PrecisionSelector from '../../components/partial/reports/precision-selector';

import cn from './bank-and-psp.module.css';

let timeout: NodeJS.Timeout;

const BankAndPspHOC = () => {
  const [dates, setDates] = useState<Date[]>([]);
  const [downloadReportVisible, setDownloadReportVisible] = useState(false);
  const [downloadReportDates, setDownloadReportDates] = useState<Date[]>([]);
  const [type, setType] = useState('without');
  const [errorList, setErrorList] = useState('');
  const [excludeRevaluation, setExcludeRevaluation] = useState<boolean>(false);
  const [currentAccountingJob, setCurrentAccountingJob] =
    useState<AccountingJobProps>();
  const [precision, setPrecision] = useState('2');

  const {
    reportsDictionaries: { activePermissions, accountingJobs },
    bankAndPspReport: {
      formationTime,
      itemsVisible,
      report,
      accountsCategories,
    },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const [getBanksReport, { isLoading, isFetching }] =
    useLazyGetBanksReportQuery();
  const [
    getAccountingJobs,
    {
      isLoading: accountingJobsIsLoading,
      isFetching: accountingJobsIsFetching,
    },
  ] = useLazyGetAccountingJobsInProgressQuery();
  const [getAccountingJobsStates] =
    useLazyGetAccountingJobsInProgressStatesQuery();

  const [
    exportBanksReport,
    { isLoading: exportInProgress, isSuccess: exportIsSuccess },
  ] = useExportBanksReportMutation();
  const [
    exportBanksReportWithAccounts,
    {
      isLoading: exportWithAccountsInProgress,
      isSuccess: exportWithAccountsIsSuccess,
    },
  ] = useExportBanksReportWithAccountsMutation();

  const { search } = useLocation();

  const navigate = useNavigate();

  const getBanksReportAction = useCallback(async (searchValue: string) => {
    const response = (await getBanksReport(searchValue)) as unknown as {
      data?: Banks;
      error?: ResponseError;
      statusCode: number;
    };

    if (response?.error) {
      getResponseErrors({
        error: response.error,
        setErrorList,
      });
    }
  }, []);

  const onClearTimeout = async () => {
    await clearTimeout(timeout);
  };

  const onGetStates = async (query: string) => {
    await onClearTimeout();

    timeout = setTimeout(async () => {
      await getAccountingJobsStates(query);
      await onGetStates(query);
    }, 20000);
  };

  const onVisibleChange = (accountType: AccountType) => {
    let nextAccountsType = ([] as AccountType[]).concat(itemsVisible);

    if (nextAccountsType.includes(accountType)) {
      nextAccountsType = nextAccountsType.filter(
        (item) => item !== accountType,
      );

      if (
        accountType === AccountType.GROUP &&
        nextAccountsType.includes(AccountType.PRIMARY_ACCOUNT)
      ) {
        nextAccountsType = [];
      }
    } else {
      nextAccountsType.push(accountType);

      if (
        accountType === AccountType.PRIMARY_ACCOUNT &&
        !nextAccountsType.includes(AccountType.GROUP)
      ) {
        nextAccountsType.push(AccountType.GROUP);
      }
    }

    dispatch(changeVisibility(nextAccountsType));
  };

  const onExportClick = async () => {
    const body: {
      createdAtFrom?: string;
      createdAtTo?: string;
      roundDecimals?: string;
    } = {};

    if (downloadReportDates && downloadReportDates.length) {
      body.createdAtFrom = new Date(downloadReportDates[0]).toISOString();

      if (downloadReportDates.length > 1) {
        body.createdAtTo = new Date(downloadReportDates[1]).toISOString();
      }
    }

    if (precision !== '2') {
      body.roundDecimals = precision;
    }

    if (type === 'without') {
      await exportBanksReport(body);
    } else if (type === 'with') {
      await exportBanksReportWithAccounts(body);
    }
  };

  useEffect(() => {
    if (exportIsSuccess || exportWithAccountsIsSuccess) {
      setDownloadReportVisible(false);
      setDownloadReportDates(dates);
      setType('without');

      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'banks-report',
        }),
      );
    }
  }, [exportIsSuccess, exportWithAccountsIsSuccess]);

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions?.includes(PermissionEnum.REPORTS_PSP_LIST) &&
      !search &&
      accountsCategories
    ) {
      const date = new Date();

      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);
      date.setMilliseconds(0);

      setDates([new Date(date)]);
      setDownloadReportDates([new Date(date)]);

      navigate(`?transferDateFrom=${format(new Date(date), 'yyyy-MM-dd')}`);
    }
  }, [activePermissions, search, accountsCategories]);

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions?.includes(PermissionEnum.REPORTS_PSP_LIST) &&
      search &&
      accountsCategories
    ) {
      const parsed = queryString.parse(search);

      const parsedDates: Date[] = [];

      if (parsed?.transferDateFrom) {
        parsedDates.push(new Date(parsed?.transferDateFrom as string));
      }

      if (parsed.transferDateTo) {
        parsedDates.push(new Date(parsed?.transferDateTo as string));
      }

      if (parsed.roundDecimals) {
        setPrecision(parsed.roundDecimals as string);
      }

      if (parsed?.excludeRevaluation) {
        setExcludeRevaluation(true);
      }

      setDates(parsedDates);
      setDownloadReportDates(parsedDates);

      getBanksReportAction(search);
    }
  }, [activePermissions, search, accountsCategories]);

  useEffect(() => {
    if (
      accountingJobs &&
      accountingJobs?.length &&
      !accountingJobsIsFetching &&
      !accountingJobsIsLoading
    ) {
      let currentJob: AccountingJobProps | undefined;

      if (accountingJobs?.find((item) => item.state === 'IN_PROGRESS')) {
        currentJob = accountingJobs?.find(
          (item) => item.state === 'IN_PROGRESS',
        );
      } else if (
        !accountingJobs?.find((item) => item.state === 'IN_PROGRESS') &&
        accountingJobs?.find((item) => item.state === 'QUEUED')
      ) {
        currentJob = accountingJobs?.find((item) => item.state === 'QUEUED');
      } else if (
        !accountingJobs?.find((item) => item.state === 'IN_PROGRESS') &&
        !accountingJobs?.find((item) => item.state === 'QUEUED') &&
        accountingJobs?.find((item) => item.state === 'NEW')
      ) {
        currentJob = accountingJobs?.find((item) => item.state === 'NEW');
      }

      if (currentJob) {
        setCurrentAccountingJob(currentJob);
      }
    }
  }, [accountingJobs, accountingJobsIsFetching, accountingJobsIsLoading]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS)
    ) {
      getAccountingJobs(
        '?type=BALANCES_LOGS&type=BALANCE_SHEET&type=REVALUATION&state=IN_PROGRESS&state=QUEUED&state=NEW&all=true',
      );

      onGetStates(
        '?type=BALANCES_LOGS&type=BALANCE_SHEET&type=REVALUATION&state=IN_PROGRESS&state=QUEUED&state=NEW&all=true',
      );
    }
  }, [activePermissions]);

  useEffect(
    () => () => {
      dispatch(clearAccountingJobs());

      setTimeout(async () => {
        await onClearTimeout();
      });
    },
    [],
  );

  useEffect(() => {
    let title = 'Banks report';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let globalContent = (
    <div className={cn.globalLoader}>
      <IconLoader />
    </div>
  );

  if (activePermissions) {
    if (
      !activePermissions.includes(PermissionEnum.REPORTS_PSP_LIST) ||
      !activePermissions.includes(PermissionEnum.SECTION_REPORTS)
    ) {
      globalContent = <PermissionDenied />;
    } else if (activePermissions.includes(PermissionEnum.REPORTS_PSP_LIST)) {
      globalContent = (
        <div className={cn.bankAndPsp}>
          <div className={cn.bankAndPspTop}>
            <div className={cn.bankAndPspTitle}>Banks report</div>
            <div className={cn.bankAndPspTopControls}>
              <div className={cn.bankAndPspTopControlsLeft}>
                <DateInputRange
                  className={cn.bankAndPspDateInput}
                  placeholder="All time"
                  values={dates.map((item) => item.toISOString())}
                  onReset={() => {
                    let link = '';

                    if (excludeRevaluation) {
                      link = `?excludeRevaluation=${excludeRevaluation}`;
                    }

                    if (precision !== '2') {
                      link = `${link}&roundDecimals=${precision}`;
                    }

                    navigate(link);

                    setDates([]);
                    setDownloadReportDates([]);
                  }}
                  onChangeRange={(dateValues) => {
                    setDates(dateValues);
                    setDownloadReportDates(dateValues);

                    let link = `?transferDateFrom=${format(
                      new Date(dateValues[0]),
                      'yyyy-MM-dd',
                    )}`;

                    if (dateValues.length > 1) {
                      link = `${link}&transferDateTo=${format(
                        new Date(dateValues[1]),
                        'yyyy-MM-dd',
                      )}`;
                    }

                    if (excludeRevaluation) {
                      link = `${link}&excludeRevaluation=${excludeRevaluation}`;
                    }

                    if (precision !== '2') {
                      link = `${link}&roundDecimals=${precision}`;
                    }

                    navigate(link);
                  }}
                />
              </div>
              <div className={cn.bankAndPspTopControlsRight}>
                <ButtonPseudo
                  text="Refresh"
                  icon={
                    <IconRefresh
                      className={report && isFetching ? cn.refreshing : ''}
                    />
                  }
                  className={`button-white ${cn.bankAndPspRefreshButton}`}
                  size={ButtonSize.SMALL}
                  onClick={() => {
                    getBanksReportAction(search);
                    setErrorList('');
                  }}
                  disabled={isLoading || isFetching}
                />
                {activePermissions?.includes(
                  PermissionEnum.REPORTS_PSP_EXPORT_ACTION,
                ) && (
                  <ButtonPseudo
                    text="Download Report"
                    icon={<IconDownload />}
                    className="button-white"
                    size={ButtonSize.SMALL}
                    onClick={() => setDownloadReportVisible(true)}
                    disabled={Boolean(errorList)}
                  />
                )}
              </div>
            </div>
            <div className={cn.banksVisibilityControls}>
              <div className={cn.banksVisibilityControlsLeft}>
                <ToggleInput
                  name="groups"
                  title="Groups"
                  checked={itemsVisible.includes(AccountType.GROUP)}
                  onChange={() => onVisibleChange(AccountType.GROUP)}
                  className={cn.banksVisibilityToggle}
                  disabled={Boolean(errorList)}
                />
                <ToggleInput
                  name="primary"
                  title="Primary Accounts"
                  checked={itemsVisible.includes(AccountType.PRIMARY_ACCOUNT)}
                  onChange={() => onVisibleChange(AccountType.PRIMARY_ACCOUNT)}
                  className={cn.banksVisibilityToggle}
                  disabled={Boolean(errorList)}
                />
                <ToggleInput
                  name="excludeRevaluation"
                  title="Exclude Revaluation"
                  checked={excludeRevaluation}
                  onChange={(e) => {
                    setExcludeRevaluation(e.target.checked);

                    let link = '';

                    if (e.target.checked) {
                      link = `${link}excludeRevaluation=${e.target.checked}`;
                    }

                    if (dates && dates?.length) {
                      link = `${link}${
                        e.target.checked ? '&' : ''
                      }transferDateFrom=${format(
                        new Date(dates[0]),
                        'yyyy-MM-dd',
                      )}`;

                      if (dates?.length > 1) {
                        link = `${link}&transferDateTo=${format(
                          new Date(dates[1]),
                          'yyyy-MM-dd',
                        )}`;
                      }
                    }

                    if (precision !== '2') {
                      link = `${link}&roundDecimals=${precision}`;
                    }

                    navigate(`?${link.replace('?', '')}`);
                  }}
                  className={cn.banksVisibilityToggle}
                  disabled={Boolean(errorList)}
                />
              </div>
              <div className={cn.banksVisibilityControlsRight}>
                <PrecisionSelector
                  precision={precision}
                  setPrecision={(value) => {
                    setPrecision(value);

                    let link = `?roundDecimals=${value}`;

                    if (dates && dates?.length) {
                      link = `${link}&transferDateFrom=${format(
                        new Date(dates[0]),
                        'yyyy-MM-dd',
                      )}`;

                      if (dates?.length > 1) {
                        link = `${link}&transferDateTo=${format(
                          new Date(dates[1]),
                          'yyyy-MM-dd',
                        )}`;
                      }
                    }

                    if (excludeRevaluation) {
                      link = `${link}&excludeRevaluation=${excludeRevaluation}`;
                    }

                    navigate(`?${link.replace('?', '')}`);
                  }}
                />
              </div>
            </div>
            <div className={cn.banksSearchInfo}>
              {formationTime?.end
                ? `Formed in ${formationTime?.duration} sec: ${new Date(
                    formationTime?.start || '',
                  ).toLocaleDateString('en-US', {
                    month: 'short',
                    year: 'numeric',
                    day: 'numeric',
                  })} from ${new Date(formationTime?.start || '')
                    .toLocaleString()
                    .slice(-8)} to ${new Date(formationTime?.end)
                    .toLocaleString()
                    .slice(-8)}`
                : 'Forming...'}
            </div>
            {activePermissions?.includes(
              PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS,
            ) && (
              <AccountingJob
                accountingJobsIsLoading={
                  accountingJobsIsLoading || accountingJobsIsFetching
                }
                accountingJob={currentAccountingJob}
                accountingJobs={accountingJobs}
                jobsLength={accountingJobs?.length}
                query="?type=BALANCES_LOGS&type=BALANCE_SHEET&type=REVALUATION&state=IN_PROGRESS&state=QUEUED&state=NEW"
                withoutMargin
              />
            )}
          </div>
          {errorList && (
            <div className={cn.reportsLoadingError}>
              <div className={cn.reportsLoadingErrorTitle}>
                Unable to load data
              </div>
              <div className={cn.reportsLoadingErrorText}>{errorList}</div>
            </div>
          )}
          {!errorList && (
            <BankAndPsp
              itemsVisible={itemsVisible}
              items={report}
              search={search}
              isLoading={isLoading || isFetching || !accountsCategories}
            />
          )}
          <SidePanel
            title="Download report"
            isOpen={downloadReportVisible}
            onClose={() => {
              setDownloadReportVisible(false);
              setDownloadReportDates(dates);
              setType('without');
            }}
            type={SidePanelType.AML}
            sidepanelExpandedKey="download-report-panel"
            footer={
              <div className={cn.statementPanelControls}>
                <ButtonPrimary
                  text={
                    exportInProgress || exportWithAccountsInProgress
                      ? 'Loading...'
                      : 'Download'
                  }
                  icon={<IconDownload />}
                  disabled={exportInProgress || exportWithAccountsInProgress}
                  onClick={onExportClick}
                  bold
                />
                <ButtonSecondary
                  className="button-white"
                  text="Close"
                  onClick={() => {
                    setDownloadReportDates(dates);
                    setDownloadReportVisible(false);
                    setType('without');
                  }}
                  bold
                />
              </div>
            }
            closeOnClickOutside
          >
            <>
              <div className={cn.exportTypeTitle}>Export type</div>
              <div className={cn.radioWrapper}>
                <Checkbox
                  name="type"
                  type="radio"
                  value="without"
                  title="Without accounts"
                  checked={type === 'without'}
                  onChange={() => setType('without')}
                />
              </div>
              <div className={cn.radioWrapper}>
                <Checkbox
                  name="type"
                  type="radio"
                  value="with"
                  title="With accounts"
                  checked={type === 'with'}
                  onChange={() => setType('with')}
                />
              </div>
              <div
                className={`${cn.datesWrapper} ${
                  !downloadReportDates.length ? cn.empty : ''
                }`}
              >
                <div className={cn.dateInputTitle}>Report period</div>
                <DateInputRange
                  values={downloadReportDates?.map((item) =>
                    item.toISOString(),
                  )}
                  onChangeRange={(dateValues) =>
                    setDownloadReportDates(dateValues)
                  }
                  placeholder="Select date"
                  withoutTwoPickers
                  withoutCustomSelector
                />
              </div>
            </>
          </SidePanel>
        </div>
      );
    }
  }

  return globalContent;
};

export default BankAndPspHOC;
